import { Button, Input, Card, Form, Upload, Typography, Grid, Spin, Space } from "antd";
import { Upload as UploadIcon, FileText, CheckCircle, TrendingUp, BarChart3, TriangleAlert } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useFileUpload from "../../hooks/useFileUpload";
import useFileProcess from "../../hooks/useFileProcess";
import ProgressModal from "../ProgressModal";
import * as XLSX from "xlsx";
import { useMemo, useState } from "react";
import { clearUploadState, resetAll, setLocalFileMeta } from "../../store/slices/fileSlice";
import { useDataReviewLogic } from "../../hooks/useDataReviewLogic";
import "./FileUploadAndProcessForm.css";

const { Dragger } = Upload;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const FileUploadAndProcessForm = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const [sheetNames, setSheetNames] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [parsing, setParsing] = useState(false);
  const localFileMeta = useSelector((state) => state.file.localFileMeta);
  const maxUploadFileSize = useSelector((state) => state.auth.maxUploadFileSize);

  const {
    loading,
    error,
    uploadedFilePath,
    processing,
    processError,
    activeStep,
    totalSteps,
  } = useSelector((state) => state.file);

  const {
    processResponse,
    hasProcessResponse,
  } = useDataReviewLogic();

  const { handleFileUpload , handleCancel} = useFileUpload(form, dispatch);
  const { handleFileProcess } = useFileProcess(dispatch, navigate);

  const MAX_FILE_SIZE_MB = maxUploadFileSize || 40;

  const handleRemoveUploaded = () => {
    // clear redux upload state
    dispatch(resetAll());
    // clear local UI state
    setSheetNames(null);
    setSelectedSheet(null);
  };

  const handleUpgrade = () => {
    navigate("/dashboard/subscription");
  };

  const uploadProps = useMemo(
    () => ({
      multiple: false,
      maxCount: 1,
      accept: ".csv,.xlsx,.xls,.xlsb",
      showUploadList: false,
      disabled: loading || processing || parsing,

      beforeUpload: async (file) => {
        // Clear previous state on new upload for a clean slate
        dispatch(resetAll());
        setSheetNames(null);
        setSelectedSheet(null);

        const isLt25MB = file.size / 1024 / 1024 <= MAX_FILE_SIZE_MB;
        if (!isLt25MB) {
          toast.error(`File must be smaller than ${MAX_FILE_SIZE_MB}MB!`,{theme: "colored"});
          return Upload.LIST_IGNORE;
        }

        dispatch(
          setLocalFileMeta({
            name: file.name,
            size: file.size,
          })
        );

        handleFileUpload(file);

        const lower = (file.name || "").toLowerCase();
        const looksLikeExcel =
          lower.endsWith(".xlsx") ||
          lower.endsWith(".xls") ||
          lower.endsWith(".xlsb");

        if (!looksLikeExcel) {
          // Not an Excel workbook — proceed normally
          return false;
        }

        setParsing(true);
        try {
          const ab = await file.arrayBuffer();
          // read workbook metadata (fast)
          const wb = XLSX.read(ab, {
            type: "array",
            bookFiles: false,
            bookProps: false,
          });
          const names = wb?.SheetNames ?? [];

          if (names.length > 1) {
            setSheetNames(names);
            setSelectedSheet(names[0]);
            toast.info(
              "File contains multiple sheets — now taking the first sheet.",{theme: "colored"}
            );
          } else if (names.length === 1) {
            setSheetNames(names);
            setSelectedSheet(names[0]);
          } else {
            setSheetNames([]); // Still useful to indicate we've parsed and found no sheets
          }
        } catch (err) {
          console.error("Failed to parse workbook in beforeUpload:", err);
          toast.warn(
            "Could not inspect Excel sheets in the browser. Proceeding with upload.",{theme: "colored"}
          );
        } finally {
          setParsing(false);
        }

        // return false to keep manual upload flow
        return false;
      },
    }),
    [loading, processing, parsing, handleFileUpload, dispatch]
  );

  const handleFinish = async (values) => {
    if (!uploadedFilePath) {
      toast.error("Please upload a file first.",{theme: "colored"});
      return;
    }

    try {
      await handleFileProcess(uploadedFilePath, {
        ...values,
        sheetName: selectedSheet,
      });
    } catch (err) {
      toast.error(err || "Processing failed", {
        duration: 10000,
        theme: "dark"
      });
    }
  };

  return (
    <>
      <PageHeader screens={screens} />
      <ProgressModal
        loading={loading}
        processing={processing}
        activeStep={activeStep}
        totalSteps={totalSteps}
        disableClose={!!(loading || processing)}
        onClose={() => {}}
        onCancel={handleCancel}
      />
      <UploadCard hasProcessResponse={hasProcessResponse}>
        <UploadForm
          form={form}
          onFinish={handleFinish}
          uploadProps={uploadProps}
          uploadedFilePath={uploadedFilePath}
          error={error}
          processing={processing}
          loading={loading}
          processError={processError}
          parsing={parsing}
          sheetNames={sheetNames}
          selectedSheet={selectedSheet}
          localFileMeta={localFileMeta}
          onRemoveUpload={handleRemoveUploaded}
          MAX_FILE_SIZE_MB={MAX_FILE_SIZE_MB}
          onUpgrade={handleUpgrade}
        />
        
        {/* Right Panel - Shows when hasProcessResponse is true */}
        {/* {hasProcessResponse && (
          <ProcessResultPanel
            navigate={navigate}
            processResponse={processResponse}
          />
        )} */}
      </UploadCard>
    </>
  );
};


export const PageHeader = ({ screens }) => (
  <div className="relative text-center mb-6 pt-5 px-4 md:px-0">
    <Title
      level={2}
      className="!text-white font-bold !mb-2 text-4xl leading-tight"
      style={{ fontSize: screens?.xs ? "1.5rem" : "2rem" }}
    >
      <span className="bg-gradient-to-r from-purple-300 via-violet-400 to-purple-500 bg-clip-text text-transparent">XynexAI</span> | AI Audit Platform
    </Title>
    <Text className="text-gray-200 text-base md:text-lg font-semibold leading-relaxed max-w-3xl mx-auto block">
      Upload your datasets and let our advanced analytics engines transform
      raw data into actionable insights
    </Text>
  </div>
);

const UploadCard = ({ children, hasProcessResponse }) => (
  <div className={`grid gap-6 transition-all duration-500 ${hasProcessResponse ? 'grid-cols-1 max-w-xl' : 'grid-cols-1 max-w-xl'}  mx-auto px-4`}>
    {children}
  </div>
);

const ProcessResultPanel = ({ navigate, processResponse }) => {
  
  const insights = processResponse?.insights;
  const topMetrics = insights?.top_metrics;
  const keyInsights = insights?.key_insights || [];
  const risks = insights?.risks_or_issues || [];

  const formatMetricLabel = (key) => {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};


  const formatMetricValue = (key, value) => {
  if (value == null) return "-";

  // Case 1: Object with range & volatility
  if (typeof value === "object" && !Array.isArray(value)) {
    const { range, volatility } = value;

    if (typeof range === "number" && typeof volatility === "number") {
      return `${range.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} (Vol: ${volatility})`;
    }

    // fallback for unknown object shapes
    return JSON.stringify(value);
  }

  // Case 2: Number
  if (typeof value === "number") {
    if (
      key.toLowerCase().includes("price") ||
      key.toLowerCase().includes("value")
    ) {
      return `$${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return value.toLocaleString();
  }

  // Case 3: String / boolean
  return String(value);
};

const shouldDisplayMetric = (key, value) => {
  if (value == null) return false;

  if (typeof value === "object") {
    if ("range" in value && "volatility" in value) {
      return value.range !== 0 || value.volatility !== 0;
    }
    return false;
  }

  if (typeof value === "string") return false;
  return true;
};


  return (
    <Card
      title={
        <div className="flex items-center gap-2 text-white">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <span>Processing Complete</span>
        </div>
      }
      className="custom-card bg-[#131314] rounded-2xl animate-slideIn border border-gray-700"
      actions={[
        <Button
          type="primary"
          className="w-11/12 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 border-0 h-12 text-base font-semibold"
          onClick={() => navigate("/dashboard/upload-result")}
          icon={<TrendingUp className="w-5 h-5" />}
        >
          View Detailed Insights
        </Button>,
      ]}
    >
      <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
        {/* Top Metrics */}

        {topMetrics && (
          <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-xl p-4 border border-purple-500/30">
            <h3 className="text-white font-semibold mb-3 text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Key Metrics
            </h3>

            <div className="grid grid-cols-2 gap-3">
              {Object.entries(topMetrics)
                .filter(([key, value]) => shouldDisplayMetric(key, value))
                .map(([key, value]) => (
                  <div key={key}>
                    <p className="text-gray-400 text-xs mb-1">
                      {formatMetricLabel(key)}
                    </p>
                    <p className="text-white font-semibold text-lg">
                      {formatMetricValue(key, value)}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Key Insights */}
        {keyInsights.length > 0 && (
          <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700">
            <h3 className="text-white font-semibold mb-3 text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              Key Insights
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
              {keyInsights.slice(0, 7).map((insight, index) => (
                <div key={index} className="flex gap-2 text-xs">
                  <span className="text-blue-400 font-bold mt-0.5 flex-shrink-0">
                    {index + 1}.
                  </span>
                  <p className="text-gray-300 leading-relaxed">{insight}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risks & Issues */}
        {risks.length > 0 && (
          <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-xl p-4 border border-red-500/30">
            <h3 className="text-white font-semibold mb-3 text-sm flex items-center gap-2">
              <TriangleAlert className="w-4 h-4 text-red-500" />
              Risks & Issues
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
              {risks.slice(0, 4).map((risk, index) => (
                <div key={index} className="flex gap-2 text-xs">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0">
                    ⚠
                  </span>
                  <p className="text-gray-300 leading-relaxed">{risk}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

const UploadForm = ({
  form,
  onFinish,
  uploadProps,
  uploadedFilePath,
  error,
  processing,
  loading,
  processError,
  parsing,
  sheetNames,
  selectedSheet,
  localFileMeta,
  onRemoveUpload,
  MAX_FILE_SIZE_MB,
  onUpgrade
}) => (
  <Card
    title={
      <div className="flex items-center gap-2 text-white">
        <FileText className="w-5 h-5" />
        <span>Upload New File</span>
      </div>
    }
    className="custom-card bg-[#131314] rounded-2xl border border-gray-700"
    actions={[
      <Button
        type="primary"
        htmlType="submit"
        className="w-11/12 h-12 text-white bg-green-600 hover:!bg-green-700 disabled:!text-white disabled:!bg-[#9161ad] disabled:hover:!bg-[#BC9BCF]/90 font-serif"
        loading={processing || loading}
        disabled={!uploadedFilePath || loading}
        onClick={() => form.submit()}
      >
        {processing ? "Submitting..." : "Submit"}
      </Button>,
    ]}
  >
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <FormInputs />
      <FileUploader
        uploadProps={uploadProps}
        uploadedFilePath={uploadedFilePath}
        error={error}
        parsing={parsing}
        sheetNames={sheetNames}
        selectedSheet={selectedSheet}
        localFileMeta={localFileMeta}
        onRemoveUpload={onRemoveUpload}
        MAX_FILE_SIZE_MB={MAX_FILE_SIZE_MB}
        onUpgrade={onUpgrade}
      />

      {processError && (
        <p className="text-red-500 mt-2">Submit error: {processError}</p>
      )}
    </Form>
  </Card>
);

const FormInputs = () => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FormItem name="fileName" label="File Name" placeholder="Enter file name" />
      <FormItem name="processName" label="Process" placeholder="Enter Process Name" />
    </div>
    <FormItem name="subProcess" label="Sub process" placeholder="Enter Sub Process Name" />
  </>
);

const FormItem = ({ name, label, placeholder }) => (
  <Form.Item label={<span className="text-gray-200 text-base">{label}</span>} name={name}>
    <Input
      placeholder={placeholder}
      className="h-10 bg-gray-50 border-gray-200 rounded-lg"
      allowClear
    />
  </Form.Item>
);

// helper for nice file size
const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const FileUploader = ({
  uploadProps,
  uploadedFilePath,
  error,
  parsing,
  sheetNames,
  selectedSheet,
  localFileMeta,
  onRemoveUpload,
  MAX_FILE_SIZE_MB,
  onUpgrade,
}) => (
  <Form.Item
    label={<span className="text-gray-200 text-base">Upload Dataset</span>}
    required
  >
    <Dragger
      {...uploadProps}
      className="rounded-lg border-dashed border-slate-600 bg-slate-800/30 py-6 sm:py-10"
    >
      <p className="flex justify-center">
        <UploadIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-200" />
      </p>
      <p className="text-base sm:text-lg text-gray-200">
        Click or drag file to this area to upload
      </p>
      <p className="text-gray-200 text-sm sm:text-base">
        File uploads immediately after selection.
      </p>
      <p className="text-violet-300 text-sm sm:text-sm">
        Supported: <span className="text-gray-200">.csv,.xlsx,.xls,.xlsb</span>{" "}
        <br />
        Max file size:{" "}
        <span className="text-gray-200">{MAX_FILE_SIZE_MB} MB</span>
      </p>
    </Dragger>

    {/* UX feedback for parsing / sheet selection / local meta */}
    <div className="mt-3">
      {parsing && (
        <div className="flex items-center gap-3">
          <div style={{ marginBottom: 12 }}>
            <Spin tip="Parsing workbook..." />
          </div>
          <Text className="text-gray-200">
            Inspecting workbook — reading sheets…
          </Text>
        </div>
      )}

      {!parsing && localFileMeta && (
        <div className="mt-2">
          <Space direction="vertical" size={2}>
            <Text className="text-gray-200">
              File:{" "}
              <span className="font-mono text-green-300">
                {localFileMeta.name}
              </span>
            </Text>
            <Text className="text-gray-200">
              Size:{" "}
              <span className="font-mono text-gray-300">
                {formatBytes(localFileMeta.size)}
              </span>
            </Text>
            {Array.isArray(sheetNames) && sheetNames.length > 1 && (
              <Text className="text-yellow-300">
                Selected sheet:{" "}
                <span className="font-mono">{selectedSheet}</span> — file has{" "}
                {sheetNames.length} sheets.
              </Text>
            )}
            {Array.isArray(sheetNames) && sheetNames.length === 1 && (
              <Text className="text-gray-200">
                Selected sheet:{" "}
                <span className="font-mono">{selectedSheet}</span>
              </Text>
            )}
            {sheetNames && sheetNames.length === 0 && (
              <Text className="text-red-400">
                No sheets detected in workbook.
              </Text>
            )}
          </Space>
        </div>
      )}

      {(uploadedFilePath || localFileMeta)  && (
        <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-green-600 text-base break-words break-all">
            Uploaded ✓ filePath:{" "}
            <span className="font-mono">{uploadedFilePath}</span>
          </p>
          <Button
            size="small"
            danger
            onClick={onRemoveUpload}
            className="!w-full sm:!w-auto"
          >
            Remove file
          </Button>
        </div>
      )}
      {error && (
        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <div className="flex items-start gap-3 text-red-400 font-medium">
            <TriangleAlert className="w-6 h-6 mt-1 shrink-0" />
            <p className="text-sm sm:text-base">{error}</p>
          </div>

          {typeof error === "string" &&
            (error.toLowerCase().includes("upgrade") ||
              error.toLowerCase().includes("limit")) && (
              <div className="mt-4 flex justify-center">
                <Button
                  onClick={onUpgrade}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white border-0 shadow-[0_0_15px_rgba(168,85,247,0.4)] hover:shadow-[0_0_25px_rgba(168,85,247,0.6)] w-full sm:w-auto h-10 px-6 font-bold transform hover:-translate-y-0.5 transition-all duration-300 rounded-full"
                >
                  Upgrade Plan 🚀
                </Button>
              </div>
            )}
        </div>
      )}
    </div>
  </Form.Item>
);


export default FileUploadAndProcessForm;